import {Injectable} from '@angular/core';
import {NGXLogger} from 'ngx-logger';
import {environment} from '../../environments/environment';
import {AlertController, ToastController} from '@ionic/angular';
import {FirebaseMessaging, GetTokenOptions} from '@capacitor-firebase/messaging';
import {Badge} from '@capawesome/capacitor-badge';
import {Store} from '@ngrx/store';
import {notificationToken, setCampaign} from 'gamesis-state';
import {Capacitor} from '@capacitor/core';

@Injectable({providedIn: 'root'})
export class NotificationProvider {
    constructor(private toastController: ToastController,
                private alertController: AlertController,
                private store: Store,
                private logger: NGXLogger) {
    }

    init(): void {
        // Request permission to use push notifications
        // iOS will prompt user and return if they granted permission or not
        // Android will just grant without prompting
        FirebaseMessaging.requestPermissions().then(result => {
            switch (result.receive) {
                case 'granted':
                    this.logger.debug('notification granted');

                    const getToken = (options: GetTokenOptions) => {
                        this.logger.debug(`requesting token`);
                        FirebaseMessaging.getToken(options).then(value => {
                            this.logger.debug(`received token ${value.token}`);
                            this.store.dispatch(notificationToken({token: value.token}));
                        }, reason => {
                            this.logger.error(`unable to get token ${reason}`);
                        });
                    };

                    if (Capacitor.isNativePlatform()) {
                        getToken({
                            vapidKey: environment.firebase.vapidKey
                        });
                    } else {
                        navigator.serviceWorker.register('firebase-messaging-sw.js').then(registration => {
                            getToken({
                                vapidKey: environment.firebase.vapidKey,
                                serviceWorkerRegistration: registration
                            });
                        });
                    }
                    break;
                default:
                    this.logger.warn('unable to register notification');
            }
        }, error => {
            this.logger.error(`unable to request permission ${error}`);
        });

        // Show us the notification payload if the app is open on our device
        FirebaseMessaging.addListener('notificationReceived', (event) => {
            Badge.increase().then(() => {
                this.logger.debug(`badge count updated`);
            });
            this.logger.debug(`received notification`);
        });

        // Method called when tapping on a notification
        FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
            this.logger.debug(`received notification action `);
            if (event.notification.data['campaign']) {
                this.store.dispatch(setCampaign({
                    id: event.notification.data['campaign'] as string
                }));
            }
        });

        this.logger.debug('initialized notification provider');
    }

    private toast(message: string, color: string): void {
        if (message) {
            this.toastController.create({
                message: message,
                position: 'bottom',
                color: color,
                duration: 2500
            }).then(toast => toast.present());
        }
    }

    public success(message?: string): void {
        if (message) {
            this.toast(message, 'success');
        }
    }

    public info(message?: string): void {
        if (message) {
            this.toast(message, 'primary');
        }
    }

    public error(message: string | null): void {
        if (message) {
            this.alertController.create({
                message: message,
                buttons: [{
                    text: 'OK',
                    role: 'cancel',
                }]
            }).then(alert => alert.present());
        }
    }
}
